import { useClerk, useUser } from '@clerk/clerk-react';
import {
  BookOpen,
  Brain,
  ChevronDownIcon,
  FileText,
  Folder,
  FolderOpen,
  Grid2X2,
  Grid2x2Check,
  Grid3x3,
  History,
  LayoutList,
  Link2,
  Lock,
  LogOut,
  MessageCircle,
  PanelRight,
  Settings,
  SquareTerminal,
} from 'lucide-react';
import { useState, type ComponentType, type FC } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useUserProfileApi } from '@/api/users';
import QalicoLogo from '@/assets/qalico-logo.svg?react';
import { ContactUsModal } from '@/components/ContactUsModal';
import { ConsultantPartnershipBadge } from '@/components/layout/ConsultantPartnershipBadge';
import { Button } from '@/components/ui/button';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar-nav';
import { Text } from '@/components/ui/text';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { UpgradeButton } from '@/components/ui/upgrade-button';
import {
  ADMIN_PATH,
  CONSULTANT_PATH,
  chatPaths,
  paths,
  regulatoryWatchPaths,
} from '@/paths';
import { useWorkspaceFeaturesService } from '@/services/workspaceFeatures';
import { useWorkspacesForSidebarService } from '@/services/workspaces';
import { cn } from '@/utils/classUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/drodown-menu-custom';
import { Skeleton } from '../ui/skeleton';

const getBottomNavigationItems = (
  isChatActivated: boolean
): { name: string; href: string; icon: typeof Settings }[] => {
  const items: { name: string; href: string; icon: typeof Settings }[] = [
    {
      name: 'Settings',
      href: 'settings',
      icon: Settings,
    },
  ];

  if (isChatActivated) {
    items.push({
      name: 'Knowledge Base',
      href: paths.sources,
      icon: Brain,
    });
  }

  return items;
};

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

function routeIsActive(pathname: string, href: string, end: boolean): boolean {
  if (end) {
    return pathname === href;
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function Sidebar({ isCollapsed, onToggle }: SidebarProps) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { data: userProfile } = useUserProfileApi();
  const { workspaces } = useWorkspacesForSidebarService();
  const { workspaceUname: currentWorkspaceUname } = useParams();
  const {
    isChatEnabled,
    isLiteratureReviewEnabled,
    isTableViewEnabled,
    isRegulatoryWatchEnabled,
    isMcpEnabled,
    isChatLocked,
    isLiteratureReviewLocked,
    isTableViewLocked,
    isRegulatoryWatchLocked,
    isPending,
    tier,
  } = useWorkspaceFeaturesService();
  const isDittoUser = userProfile?.type === 'ditto';
  const isConsultant = userProfile?.type === 'consultant';

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalTitle, setContactModalTitle] = useState<
    string | undefined
  >(undefined);
  const navigate = useNavigate();
  const location = useLocation();

  const isChatActivated = isChatEnabled || isChatLocked;
  const w = currentWorkspaceUname ?? '';
  const base = `/workspace/${w}`;

  const bottomNavigationItems = getBottomNavigationItems(isChatActivated);

  const onNavigateToWorkspace = (workspaceUname: string) => () => {
    if (workspaceUname === currentWorkspaceUname) {
      return;
    }
    navigate(`/workspace/${workspaceUname}`);
    window.location.reload();
  };

  const rwLocked = isRegulatoryWatchLocked;
  const chatLocked = isChatLocked;
  const tablesLocked = isTableViewLocked;
  const litLocked = isLiteratureReviewLocked;

  const showRegulatory = isRegulatoryWatchEnabled || isRegulatoryWatchLocked;
  const showChat = isChatEnabled || isChatLocked;
  const showTables = isTableViewEnabled || isTableViewLocked;
  const showProjects =
    (isChatEnabled || isChatLocked) &&
    (isTableViewEnabled || isTableViewLocked);
  const projectsLocked = isChatLocked || isTableViewLocked;
  const showLit = isLiteratureReviewEnabled || isLiteratureReviewLocked;
  const showMcp = isMcpEnabled;
  const isMcpOnly =
    isMcpEnabled && !showRegulatory && !showChat && !showTables && !showLit;

  const linkButtonClass =
    'w-full justify-start gap-2 rounded-lg font-light hover:bg-ditto-500 hover:font-medium data-[active=true]:bg-yellow-400 data-[active=true]:font-medium';

  const LockedRow: FC<{
    label: string;
    icon: ComponentType<{ className?: string }>;
    unlockLabel: string;
  }> = ({ label, icon: Icon, unlockLabel }) => (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => {
              setContactModalTitle(unlockLabel);
              setContactModalOpen(true);
            }}
            className={cn(
              'flex w-full items-center rounded-lg px-2 py-2 text-sm font-light transition-colors text-muted-foreground/50',
              isCollapsed ? 'justify-center' : 'gap-2'
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left">{label}</span>
                <Lock className="h-3.5 w-3.5 shrink-0 text-blue-400" />
              </>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>Upgrade to Pro</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const NavRow: FC<{
    to: string;
    end?: boolean;
    icon: ComponentType<{ className?: string }>;
    label: string;
  }> = ({ to, end = false, icon: Icon, label }) => {
    const href = to.startsWith('/') ? to : `${base}/${to}`;
    // On detail pages, location state carries the origin sidebar path
    const activeSidebarPath = (location.state as { backPath?: string } | null)
      ?.backPath;
    const active =
      routeIsActive(location.pathname, href, end) ||
      (activeSidebarPath !== undefined && activeSidebarPath === href);

    const inner = (
      <SidebarMenuButton asChild isActive={active} className={linkButtonClass}>
        <NavLink to={href} end={end} title={isCollapsed ? label : undefined}>
          <Icon className="h-4 w-4 shrink-0" />
          {!isCollapsed && <span>{label}</span>}
        </NavLink>
      </SidebarMenuButton>
    );

    return (
      <SidebarMenuItem>
        {isCollapsed ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>{inner}</TooltipTrigger>
              <TooltipContent side="right">{label}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          inner
        )}
      </SidebarMenuItem>
    );
  };

  return (
    <div
      className={cn(
        'flex h-full flex-col border-r border-border bg-ditto-200 transition-all duration-300 ease-in-out',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex h-fit min-h-16 items-center justify-between p-2">
        {!isCollapsed && (
          <div className="flex items-center py-4 px-3">
            <QalicoLogo />
          </div>
        )}
        <div
          className={cn(
            'flex items-center',
            isCollapsed ? 'flex-col-reverse items-center px-2' : 'space-x-2'
          )}
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className={cn(
              'h-8 w-8 transition-all duration-300 hover:bg-muted',
              isCollapsed ? 'mx-auto' : '',
              !isCollapsed && 'rotate-180'
            )}
          >
            <PanelRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {(() => {
        const currentWorkspace = workspaces?.find(
          w => w.uname === currentWorkspaceUname
        );
        const consultantOrg = currentWorkspace?.consultantOrganization;
        if (!consultantOrg || isCollapsed) return null;
        return (
          <ConsultantPartnershipBadge
            name={consultantOrg.name}
            logo={consultantOrg.logo}
          />
        );
      })()}
      {workspaces?.length && workspaces.length > 1 && !isCollapsed && (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <FlexRow
              alignItems="center"
              justifyContent="between"
              br="md"
              h="8"
              className="border border-neutral-200 shadow-sm m-2 gap-2 px-2 py-1 flex-shrink-0"
            >
              <FlexRow
                alignItems="center"
                className="gap-2 px-2 py-1 w-full flex-shrink"
              >
                <Folder className="h-4 w-4" />
                <Ellipsis hasTooltip asChild>
                  <Text
                    variant="body-sm"
                    color="primary"
                    className="text-nowrap"
                  >
                    {
                      workspaces.find(
                        workspace => workspace.uname === currentWorkspaceUname
                      )?.name
                    }
                  </Text>
                </Ellipsis>
              </FlexRow>
              <ChevronDownIcon size={16} />
            </FlexRow>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="start">
            {workspaces.map(workspace => (
              <DropdownMenuItem
                key={workspace.id}
                onClick={onNavigateToWorkspace(workspace.uname)}
              >
                {workspace.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Separator />

      <ScrollArea className="flex-1 px-1 py-2">
        <nav className="flex flex-col gap-1">
          {isPending ? (
            <FlexCol gap="6">
              <FlexCol gap="2">
                <Skeleton className="w-full h-8" />
                <Skeleton className="w-full h-8" />
              </FlexCol>
              <FlexCol gap="2">
                <Skeleton className="w-3/5 h-4" />
                <Skeleton className="w-full h-8" />
                <Skeleton className="w-full h-8" />
              </FlexCol>
            </FlexCol>
          ) : (
            <>
              {showRegulatory && (
                <SidebarGroup>
                  {!isCollapsed && (
                    <SidebarGroupLabel className="px-2">
                      Regulatory watch
                    </SidebarGroupLabel>
                  )}
                  <SidebarMenu>
                    {rwLocked ? (
                      <>
                        <SidebarMenuItem>
                          <LockedRow
                            label="My references"
                            icon={FileText}
                            unlockLabel="Unlock Regulatory Watch"
                          />
                        </SidebarMenuItem>
                        <SidebarMenuItem>
                          <LockedRow
                            label="My updates"
                            icon={Grid2X2}
                            unlockLabel="Unlock Regulatory Watch"
                          />
                        </SidebarMenuItem>
                        <SidebarMenuItem>
                          <LockedRow
                            label="Audit table"
                            icon={Grid2x2Check}
                            unlockLabel="Unlock Regulatory Watch"
                          />
                        </SidebarMenuItem>
                        <SidebarMenuItem>
                          <LockedRow
                            label="My updates by status"
                            icon={LayoutList}
                            unlockLabel="Unlock Regulatory Watch"
                          />
                        </SidebarMenuItem>
                      </>
                    ) : (
                      <>
                        <NavRow
                          to={`${base}/${paths.regulatoryWatch}/${regulatoryWatchPaths.allDocuments}`}
                          end
                          icon={FileText}
                          label="My references"
                        />
                        <NavRow
                          to={`${base}/${paths.regulatoryWatch}/${regulatoryWatchPaths.allUpdates}`}
                          end
                          icon={Grid2X2}
                          label="My updates"
                        />
                        <NavRow
                          to={`${base}/${paths.regulatoryWatch}/${regulatoryWatchPaths.auditTable}`}
                          end
                          icon={Grid2x2Check}
                          label="Audit table"
                        />
                        <NavRow
                          to={`${base}/${paths.regulatoryWatch}/${regulatoryWatchPaths.newsfeed}`}
                          end
                          icon={LayoutList}
                          label="My updates by status"
                        />
                      </>
                    )}
                  </SidebarMenu>
                </SidebarGroup>
              )}

              {showChat && (
                <SidebarGroup>
                  {!isCollapsed && (
                    <SidebarGroupLabel className="px-2">Chat</SidebarGroupLabel>
                  )}
                  <SidebarMenu>
                    {chatLocked ? (
                      <>
                        <SidebarMenuItem>
                          <LockedRow
                            label="Ask"
                            icon={MessageCircle}
                            unlockLabel="Unlock Ask"
                          />
                        </SidebarMenuItem>
                        <SidebarMenuItem>
                          <LockedRow
                            label="History"
                            icon={History}
                            unlockLabel="Unlock Ask"
                          />
                        </SidebarMenuItem>
                      </>
                    ) : (
                      <>
                        <NavRow
                          to={`${base}/${paths.ask}/${chatPaths.askNew}`}
                          end
                          icon={MessageCircle}
                          label="Ask"
                        />
                        <NavRow
                          to={`${base}/${paths.ask}/${chatPaths.askHistory}`}
                          end
                          icon={History}
                          label="History"
                        />
                      </>
                    )}
                  </SidebarMenu>
                </SidebarGroup>
              )}

              {(showTables || showProjects || showLit) && (
                <SidebarGroup>
                  {!isCollapsed && (
                    <SidebarGroupLabel className="px-2">
                      More tools
                    </SidebarGroupLabel>
                  )}
                  <SidebarMenu>
                    {showTables &&
                      (tablesLocked ? (
                        <SidebarMenuItem>
                          <LockedRow
                            label="Tables"
                            icon={Grid3x3}
                            unlockLabel="Unlock Tables"
                          />
                        </SidebarMenuItem>
                      ) : (
                        <NavRow
                          to={`${base}/${paths.tableViews}`}
                          end={false}
                          icon={Grid3x3}
                          label="Tables"
                        />
                      ))}
                    {showProjects &&
                      (projectsLocked ? (
                        <SidebarMenuItem>
                          <LockedRow
                            label="Projects"
                            icon={FolderOpen}
                            unlockLabel="Unlock Projects"
                          />
                        </SidebarMenuItem>
                      ) : (
                        <NavRow
                          to={`${base}/${paths.projects}`}
                          end={false}
                          icon={FolderOpen}
                          label="Projects"
                        />
                      ))}
                    {showLit &&
                      (litLocked ? (
                        <SidebarMenuItem>
                          <LockedRow
                            label="Literature Reviews"
                            icon={BookOpen}
                            unlockLabel="Unlock Literature Reviews"
                          />
                        </SidebarMenuItem>
                      ) : (
                        <NavRow
                          to={`${base}/${paths.literatureReviews}`}
                          end={false}
                          icon={BookOpen}
                          label="Literature Reviews"
                        />
                      ))}
                  </SidebarMenu>
                </SidebarGroup>
              )}
              {showMcp && (
                <SidebarGroup>
                  {!isCollapsed && (
                    <SidebarGroupLabel className="px-2">
                      Integrations
                    </SidebarGroupLabel>
                  )}
                  <SidebarMenu>
                    <NavRow
                      to={`${base}/${paths.mcp}`}
                      end={false}
                      icon={Link2}
                      label="Claude Connector"
                    />
                  </SidebarMenu>
                </SidebarGroup>
              )}
            </>
          )}
        </nav>
      </ScrollArea>

      {tier === 'free' && !isCollapsed && (
        <div className="mx-3 rounded-xl bg-blue-50 border border-blue-200 p-4">
          <p className="text-xs font-medium leading-snug text-neutral-800">
            Ready to go beyond a free plan? Upgrade for premium features.
          </p>
          <UpgradeButton
            size="md"
            className="mt-3 w-full justify-center"
            onClick={() => setContactModalOpen(true)}
          >
            Contact us
          </UpgradeButton>
        </div>
      )}

      <ContactUsModal
        open={contactModalOpen}
        onOpenChange={open => {
          setContactModalOpen(open);
          if (!open) setContactModalTitle(undefined);
        }}
        title={contactModalTitle}
        topContent={
          contactModalTitle === 'Unlock Tables' ||
          contactModalTitle === 'Unlock Literature Reviews' ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {contactModalTitle === 'Unlock Tables'
                  ? 'Discover how you can run systematic data extraction and analysis with Qalico table views:'
                  : 'Discover how Qalico helps you execute complete literature reviews in a few clicks:'}
              </p>
              <div className="aspect-video w-full overflow-hidden rounded-lg">
                <iframe
                  src="https://www.youtube.com/embed/PE3Nb_XAYfM"
                  title="Qalico overview"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
            </div>
          ) : undefined
        }
      />

      <div className="px-3 py-4">
        {bottomNavigationItems
          .filter(item => !(isMcpOnly && item.href === 'settings'))
          .map((item, index) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.href !== 'settings'}
              className={({ isActive }) =>
                cn(
                  'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isCollapsed ? 'justify-center' : 'space-x-3',
                  index > 0 && 'mt-2',
                  isActive
                    ? 'bg-yellow-400 font-medium'
                    : 'hover:bg-ditto-500 hover:font-medium font-light'
                )
              }
              title={isCollapsed ? item.name : undefined}
            >
              <item.icon className="h-4 w-4" />
              {!isCollapsed && <span>{item.name}</span>}
            </NavLink>
          ))}
        {isDittoUser && (
          <>
            <Separator className="mb-2 mt-4" />
            <NavLink
              to={ADMIN_PATH}
              className={cn(
                'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isCollapsed ? 'justify-center' : 'space-x-3',
                'hover:bg-ditto-500 hover:font-medium font-light'
              )}
              title={isCollapsed ? 'Back office' : undefined}
            >
              <SquareTerminal className="h-4 w-4" />
              {!isCollapsed && <span>Back office</span>}
            </NavLink>
          </>
        )}
        {isConsultant && userProfile?.consultantOrganizationId && (
          <>
            <Separator className="mb-2 mt-4" />
            <NavLink
              to={`${CONSULTANT_PATH}/${userProfile.consultantOrganizationId}`}
              className={cn(
                'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isCollapsed ? 'justify-center' : 'space-x-3',
                'hover:bg-ditto-500 hover:font-medium font-light'
              )}
              title={isCollapsed ? 'Consultant portal' : undefined}
            >
              <SquareTerminal className="h-4 w-4" />
              {!isCollapsed && <span>Consultant portal</span>}
            </NavLink>
          </>
        )}
        <Separator className="mb-4 mt-2" />
        {user && (
          <div className="space-y-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => signOut()}
              className={cn(
                'w-full justify-start text-muted-foreground hover:text-foreground',
                isCollapsed ? 'justify-center px-2' : 'px-3'
              )}
              title={isCollapsed ? 'Sign out' : undefined}
            >
              <LogOut className="h-4 w-4" />
              {!isCollapsed && <span className="ml-2">Sign out</span>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
